// Vuetify
import "@mdi/font/css/materialdesignicons.css";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { md3 } from "vuetify/blueprints/md3";
import { Whitelabel } from "./types/Whitelabel";
import { ref } from "vue";
import { useFavicon } from "@vueuse/core";
import api from "./api.ts";

const icon = useFavicon();

export const LOGO_LIGHT = ref<string>(null);
export const LOGO_DARK = ref<string>(null);

export const THEME_NAMES = {
  LIGHT: "lightTheme",
  DARK: "darkTheme",
  ADMIN_LIGHT: "adminLightTheme",
  ADMIN_DARK: "adminDarkTheme",
} as const;


const getWhitelabelTheme = async () => {
  return api
    .post<Whitelabel | null>("/api/whitelabel/theme", {
      host: window.location.hostname,
    })
    .then((response) => {
      if (response.data !== null) {
        LOGO_LIGHT.value = response.data.logo.dark;
        LOGO_DARK.value = response.data.logo.light;
        icon.value = response.data.favicon;

        const root = document.documentElement;

        root.style.setProperty(`--bg-image-light`, `url(${response.data.background_image.light}`);
        root.style.setProperty(`--bg-image-dark`, `url(${response.data.background_image.dark}`);

        return {
          [THEME_NAMES.LIGHT]: {
            dark: false,
            colors: {
              primary: response.data.colors.primary,
              secondary: response.data.colors.secondary,
              tertiary: response.data.colors.tertiary,
              status_active: "#27AE60",
              status_canceled: "#CC2132",
              status_lead: "#F2AE01",
            },
          },
          [THEME_NAMES.DARK]: {
            dark: true,
            colors: {
              primary: response.data.colors.primary,
              secondary: response.data.colors.secondary,
              tertiary: response.data.colors.tertiary,
              status_active: "#27AE60",
              status_canceled: "#CC2132",
              status_lead: "#F2AE01",
            },
          },
        };
      } else {
        return {
          [THEME_NAMES.LIGHT]: {
            dark: false,
            colors: {
              primary: "#0071ce",
              secondary: "#202945",
              tertiary: "#FF4B1F",
              status_active: "#27AE60",
              status_canceled: "#CC2132",
              status_lead: "#F2AE01",
            },
          },
          [THEME_NAMES.DARK]: {
            dark: true,
            colors: {
              primary: "#0071ce",
              secondary: "#202945",
              tertiary: "#FF4B1F",
              status_active: "#27AE60",
              status_canceled: "#CC2132",
              status_lead: "#F2AE01",
            },
          },
        };
      }
    })
    .catch(() => {
      return {
        [THEME_NAMES.LIGHT]: {
          dark: false,
          colors: {
            primary: "#0071ce",
            secondary: "#202945",
            tertiary: "#FF4B1F",
            status_active: "#27AE60",
            status_canceled: "#CC2132",
            status_lead: "#F2AE01",
          },
        },
        [THEME_NAMES.DARK]: {
          dark: true,
          colors: {
            primary: "#0071ce",
            secondary: "#202945",
            tertiary: "#FF4B1F",
            status_active: "#27AE60",
            status_canceled: "#CC2132",
            status_lead: "#F2AE01",
          },
        },
      };
    });
};

const themes = getWhitelabelTheme();

const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

// const vuetify = createVuetify({
//   blueprint: md3,
//   components,
//   directives,
//   icons: {
//     defaultSet: "mdi", // This is already the default value - only for display purposes
//   },
//   theme: {
//     defaultTheme: "darkTheme",
//     themes: await themes.then((data) => data),
//   },
//   defaults: {
//     VBtn: { color: "default" },
//     VCheckbox: { color: "primary", density: "compact" },
//     VTextField: { density: "compact", variant: "outlined" },
//     VSelect: { density: "compact", variant: "outlined" },
//     VCombobox: { density: "compact", variant: "outlined" },
//     VTextarea: { density: "compact", variant: "outlined" },
//   },
// });

const setupVuetify = async () => {
  const resolvedThemes = await themes;

  const adminThemes = {
    [THEME_NAMES.ADMIN_LIGHT]: {
      dark: false,
      colors: {
        primary: "#0C314D",
        secondary: "#7DB7D4",
        tertiary: "#144564",
        accent: "#F2B880",
        info: "#63B3DE",
        success: "#5CC38A",
        warning: "#F0B44C",
        error: "#E06A5F",
        admin_toolbar: "#DDF4F2",
        surface: "#F7FCFC",
        background: "#EFF9F8",
        status_active: "#27AE60",
        status_canceled: "#CC2132",
        status_lead: "#F2AE01",
      },
    },
    [THEME_NAMES.ADMIN_DARK]: {
      dark: true,
      colors: {
        primary: "#70CAC6",
        secondary: "#2F6F73",
        tertiary: "#BEEAE7",
        accent: "#F4A261",
        info: "#3A86B3",
        success: "#2E8B57",
        warning: "#D98E04",
        error: "#C44536",
        admin_toolbar: "#102B3F",
        surface: "#112838",
        background: "#091A28",
        status_active: "#27AE60",
        status_canceled: "#CC2132",
        status_lead: "#F2AE01",
      },
    },
  };

  return createVuetify({
    blueprint: md3,
    components,
    directives,
    icons: {
      defaultSet: "mdi",
    },
    theme: {
      defaultTheme: prefersDark ? THEME_NAMES.DARK : THEME_NAMES.LIGHT,
      themes: {
        ...resolvedThemes,
        ...adminThemes,
      },
    },
    defaults: {
      VBtn: { color: "default" },
      VCheckbox: { color: "primary", density: "compact" },
      VTextField: { density: "compact", variant: "outlined" },
      VSelect: { density: "compact", variant: "outlined" },
      VCombobox: { density: "compact", variant: "outlined" },
      VTextarea: { density: "compact", variant: "outlined" },
    },
  });
}

const vuetify = await setupVuetify();

export default vuetify;
